import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, Loader2, Edit } from "lucide-react";
import Swal from "sweetalert2";
import CreateSubCategoryModal from "../../components/AdminComponents/subcategory/CreateSubCategoryModal";
import EditSubCategoryModal from "../../components/AdminComponents/subcategory/EditSubCategoryModal";
import SubCategorySkeleton from './../../components/AdminComponents/subcategory/SubCategorySkeleton';
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const SubCategory = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/category/fetch-all`,
        // { withCredentials: true }
      );
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch categories",
      });
    }
  };

  const fetchSubCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/subcategory/fetch-all-data`,
        // { withCredentials: true }
      );
      setSubCategories(response.data);
      setFilteredSubCategories(response.data);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch subcategories",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
  }, []);

  // Filter subcategories based on selected category
  useEffect(() => {
    let filtered = [...subCategories];

    if (selectedCategory) {
      filtered = filtered.filter((sub) => sub.category._id === selectedCategory);
    }

    setFilteredSubCategories(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [selectedCategory, subCategories]);

  // Pagination calculations
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentSubCategories = filteredSubCategories.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredSubCategories.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This subcategory will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0bb6d9",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/subcategory/delete/${id}`,
          // { withCredentials: true }
        );
        Swal.fire("Deleted!", "Subcategory has been deleted.", "success");

        // Update both subcategories arrays
        const updatedSubCategories = subCategories.filter((sub) => sub._id !== id);
        setSubCategories(updatedSubCategories);

        // Adjust current page if needed
        if (currentSubCategories.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } catch (err) {
        console.error("Error deleting subcategory:", err);
        Swal.fire("Error!", "Failed to delete subcategory.", "error");
      }
    }
  };

  const handleEdit = (subCategory) => {
    setEditingSubCategory(subCategory);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setEditingSubCategory(null);
    fetchSubCategories();
  };

  return (
    <div className="min-h-screen py-8 bg-[#fafafa]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex md:flex-row flex-col md:items-center md:justify-between gap-4 mb-8">
          {/* Left side: Heading */}
          <div className="text-center md:text-left">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              <span className="text-[#0bb6d9]">SubCategories</span>
            </h1>
            <p className="text-gray-600">
              Showing {currentSubCategories.length} of {filteredSubCategories.length} subcategories
              {selectedCategory ? " in this category" : ""}
            </p>
          </div>

          {/* Right side: Create SubCategory Button */}
          <div className="flex md:flex-row flex-col gap-4 items-center">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full md:w-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0bb6d9] focus:border-transparent outline-none transition"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.title}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                setIsEditing(false);
                setEditingSubCategory(null);
                setIsModalOpen(true);
              }}
              className="px-6 py-3 bg-[#0bb6d9] text-white rounded-lg font-semibold hover:bg-[#069ab7] transition flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create SubCategory
            </button>
          </div>
        </div>

        {/* SubCategories List */}
        <div className="overflow-hidden">
          {
            loading ? <SubCategorySkeleton /> :
              <>
                <div className="p-6">
                  {currentSubCategories.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      No subcategories found
                      {selectedCategory ? " for this category" : ""}. Create your
                      first one!
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {currentSubCategories.map((subCategory) => (
                        <div
                          key={subCategory._id}
                          className="relative p-6 bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow group duration-500"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="text-lg group-hover:text-[#0bb6d9] transition-colors duration-500 font-semibold mt-5 text-gray-800">
                              {subCategory.title}
                            </h3>
                            <div className="flex gap-2 absolute top-2 right-2">
                              <button
                                onClick={() => handleEdit(subCategory)}
                                className="px-3 py-1 bg-[#0bb6d9]  hover:bg-[#069ab7] text-white text-sm rounded"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(subCategory._id)}
                                className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            Category:{" "}
                            <span className="font-medium">
                              {subCategory.category.title}
                            </span>
                          </p>
                          <p className="text-sm text-gray-500">
                            Created:{" "}
                            {new Date(subCategory.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
          }
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-end items-center mt-8 space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <FaChevronLeft size={14} /> Prev
            </button>

            {getPageNumbers().map((page, index) => (
              <div key={index}>
                {page === "..." ? (
                  <span className="px-3 py-2 text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-md transition-colors ${currentPage === page
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    {page}
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <FaChevronRight size={14} />
            </button>
          </div>
        )}

        {/* Modal */}
        {isModalOpen && (
          <>
            {isEditing ? (
              <EditSubCategoryModal
                onClose={handleModalClose}
                categories={categories}
                subCategory={editingSubCategory}
                onUpdated={fetchSubCategories}
              />
            ) : (
              <CreateSubCategoryModal
                onClose={handleModalClose}
                categories={categories}
                onCreated={fetchSubCategories}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubCategory;